import { useOnboardingDraft } from "../OnboardingContext";
import styles from "../OnboardingWizard.module.css";
import type { StepProps } from "../OnboardingWizard";

// Mirror the backend caps in UserOnboardingController.validate - these
// answers are injected verbatim into workspace markdown, so keep them short.
const PREFERRED_NAME_MAX = 80;
const BUSINESS_DESCRIPTION_MAX = 500;
const AGENT_BOUNDARIES_MAX = 1000;

export const AboutYouStep = ({ onNext, onBack, canBack }: StepProps): JSX.Element => {
  const { draft, update } = useOnboardingDraft();

  return (
    <section className={styles.card}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>Tell us a bit about you.</h2>
        <p className={styles.stepHint}>
          These go straight into your agent's notes. All optional - it'll ask about the rest when you first chat.
        </p>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="onb-preferred-name">
          What should your agent call you?
        </label>
        <span className={styles.hint}>
          First name, nickname - whatever you'd like to hear in chat.
        </span>
        <input
          id="onb-preferred-name"
          className={styles.input}
          value={draft.preferredName ?? ""}
          onChange={(e) => update("preferredName", e.target.value.slice(0, PREFERRED_NAME_MAX) || undefined)}
          placeholder="e.g. Bob"
          maxLength={PREFERRED_NAME_MAX}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="onb-business-description">
          What does your business do?
        </label>
        <span className={styles.hint}>
          One or two sentences.
        </span>
        <textarea
          id="onb-business-description"
          className={styles.textarea}
          value={draft.businessDescription ?? ""}
          onChange={(e) => update("businessDescription", e.target.value.slice(0, BUSINESS_DESCRIPTION_MAX) || undefined)}
          placeholder="e.g. Residential electrical work across the northern beaches - quotes, installs, emergency callouts."
          maxLength={BUSINESS_DESCRIPTION_MAX}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="onb-agent-boundaries">
          Anything you'd prefer your agent avoid doing or saying?
        </label>
        <span className={styles.hint}>
          Guidance your agent will aim to follow. Like any AI it can get things wrong, so keep an eye on anything important.
        </span>
        <textarea
          id="onb-agent-boundaries"
          className={styles.textarea}
          value={draft.agentBoundaries ?? ""}
          onChange={(e) => update("agentBoundaries", e.target.value.slice(0, AGENT_BOUNDARIES_MAX) || undefined)}
          placeholder="e.g. Check with me before giving prices. Avoid contacting customers after 6pm."
          maxLength={AGENT_BOUNDARIES_MAX}
        />
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.secondaryBtn} onClick={onBack} disabled={!canBack}>Back</button>
        <button type="button" className={styles.primaryBtn} onClick={onNext}>Next</button>
      </div>
    </section>
  );
};
